/**
 * WHAT A TEACHER WRITES ONTO A DRAWING, DRAWN.
 *
 * ── THE SAME TRICK THE CURLY ARROWS USE ───────────────────────────────────────────────────────
 * OpenChemLib leaves an invisible hit target on every atom — `<circle id="m:Atom:3" cx cy>` —
 * so where each one SITS can be read straight off the rendered SVG. `mechanism-draw` uses that
 * to bow an arrow between two of them; this uses it to put electrons, a highlight and a number
 * on one of them. No DOM, no measuring, the same answer in a test as on a page, and the marks
 * live in the drawing's own coordinate space so they survive it being drawn at any size.
 *
 * It is also why these are cheap. The scope called Lewis dots on a drawn atom a blocker; they
 * are four circles at a known point.
 *
 * ── WHAT KEEPS A TEACHER'S OWN NUMBER OUT OF THE COLOURING ────────────────────────────────────
 * Not the order these run in. Colours rewrite a `<text>` element's `fill`, and everything drawn
 * HERE carries no `fill` at all — the stylesheet gives it one. So a colour pass cannot touch our
 * own marks whenever it runs, which a mutation proved by reversing the order and changing
 * nothing on the page.
 *
 * Colours still run first, because that is the order that would be right if a mark ever did
 * carry a fill of its own. The guarantee is the missing attribute; the order is only tidy.
 *
 * ── WHAT A COLOUR CAN AND CANNOT REACH ────────────────────────────────────────────────────────
 * A skeletal carbon has no label — that is what skeletal means — so there is no ink of its own
 * to colour. `colourableAtoms` says which atoms have some, and the picker offers only those
 * rather than letting a teacher choose a colour that does nothing.
 */

import {
  anchorPoints, bondEnds, bondLength, drawUnit, freeDirection, includeInViewBox, textExtent,
  viewBoxOf, type Point,
} from './mechanism-draw';
import { parseLewisValue } from './reaction-lewis';

import type { StructureAnnotations } from './structure-annotations';

/**
 * Where each species sits ACROSS the drawing, as a fraction of its width.
 *
 * A caption goes under the molecule it names, and the only thing that knows where that molecule
 * is drawn is the drawing. `parts` is `anchorFacts`' per-atom fragment number, counted from 1.
 */
export function partSpans(svg: string, parts: number[]): { part: number; centre: number }[] {
  const box = viewBoxOf(svg);
  const { atoms } = anchorPoints(svg);
  if (!box) return [];
  const xs = new Map<number, number[]>();
  atoms.forEach((p, i) => {
    const part = parts[i];
    if (!p || !part) return;
    xs.set(part, [...(xs.get(part) ?? []), p.x]);
  });
  return [...xs.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([part, list]) => ({
      part,
      centre: ((Math.min(...list) + Math.max(...list)) / 2 - box.x) / box.w,
    }));
}

/** Every `<text>` in the drawing, with where it is and how far through the string it starts. */
function texts(svg: string): { at: number; end: number; tag: string; p: Point }[] {
  const out: { at: number; end: number; tag: string; p: Point }[] = [];
  for (const m of svg.matchAll(/<text\b[^>]*>/g)) {
    const tag = m[0];
    const x = /\sx="([-\d.]+)"/.exec(tag);
    const y = /\sy="([-\d.]+)"/.exec(tag);
    if (!x || !y) continue;
    out.push({ at: m.index, end: m.index + tag.length, tag, p: { x: Number(x[1]), y: Number(y[1]) } });
  }
  return out;
}

/**
 * Which atom each piece of text belongs to: the nearest one, with no distance cap.
 *
 * An atom can own several. `[OH-]` draws as THREE `<text>` elements around one centre — the
 * symbol, its hydrogen and its charge — and a highlight on that atom means all three, or it
 * reads as a rendering fault. A cap is what made this wrong first time: the hydrogen sits a
 * little over two atom-radii out, which is further than any threshold that would also keep a
 * crowded molecule's labels apart.
 *
 * No cap is right rather than merely easier — but ONLY because every `<text>` in one of our
 * drawings is an atom's label, and that is a property of how `renderStructure` calls `toSVG`
 * rather than of OpenChemLib. It was not true when this was written: `suppressChiralText` alone
 * leaves `R`, `S`, `abs`, `?` and `Z` beside the stereocentres, and with no cap those were
 * attributed to the nearest atom — so a plain carbon vertex with no label of its own counted as
 * `colourableAtoms`, and colouring a neighbour recoloured a stereo descriptor. `suppressESR`
 * and `suppressCIPParity` are what make the sentence above true; see the comment on the
 * `toSVG` call, which says not to drop them without coming back here.
 *
 * Our own marks carry no `fill`, so a second pass over an annotated drawing cannot touch them.
 */
function ownerOf(p: Point, atoms: Point[]): number | null {
  let best: number | null = null;
  let bestDistance = Infinity;
  atoms.forEach((a, i) => {
    if (!a) return;
    const d = Math.hypot(a.x - p.x, a.y - p.y);
    if (d < bestDistance) { bestDistance = d; best = i; }
  });
  return best;
}

/** The atoms that have ink of their own, so a highlight on them would show. */
export function colourableAtoms(svg: string): number[] {
  const { atoms } = anchorPoints(svg);
  const found = new Set<number>();
  for (const t of texts(svg)) {
    const owner = ownerOf(t.p, atoms);
    if (owner !== null) found.add(owner);
  }
  return [...found].sort((a, b) => a - b);
}

/**
 * One atom's label in the teacher's colour.
 *
 * A highlight on the WHOLE drawing already exists, as a mark on the token, and it gives the
 * molecule's element hues up deliberately — a highlight says "look at this one". This is the
 * finer version of the same act and wins over it where they meet, because it is the more
 * specific thing the teacher said.
 */
export function withAtomColours(
  svg: string, colours: Record<number, string>, symbols: string[] = [],
): string {
  if (!Object.keys(colours).length) return svg;
  const { atoms } = anchorPoints(svg);

  let out = '';
  let from = 0;
  const drawn = new Set<number>();
  for (const t of texts(svg)) {
    const owner = ownerOf(t.p, atoms);
    if (owner !== null) drawn.add(owner);
    const colour = owner === null ? undefined : colours[owner];
    if (!colour) continue;
    out += svg.slice(from, t.at) + t.tag.replace(/\sfill="[^"]*"/, ` fill="${colour}"`);
    from = t.end;
  }
  const tinted = out ? out + svg.slice(from) : svg;

  /*
   * ── AN ATOM WITH NO LABEL GETS ONE, so that every atom can take a colour ──────────────────
   *
   * A skeletal carbon is a corner between two bonds and draws no text, so there was nothing for
   * a colour to land on — and the commonest molecules are nothing BUT skeletal carbons:
   * measured, 0 of 6 atoms in benzene could be tinted, 0 of 6 in cyclohexane, 0 of 10 in
   * naphthalene. The panel answered that with a note saying so, which is a limitation explained
   * rather than removed.
   *
   * It stopped being tolerable when the element hues went: with the drawing monochrome, the
   * teacher's colour is the ONLY colour, so *"the teacher can color them if they want"* has to
   * be true of every atom. Writing the `C` in is also what a chemist does when calling out a
   * particular skeletal atom, so the drawing says the same thing either way.
   *
   * Sized and placed to match the library's own symbols rather than guessed: OpenChemLib draws
   * every element at a fixed `font-size="14"` with the baseline 4.7 below the atom's centre,
   * measured across five molecules. The anchor differs — the library sets its text from the
   * left edge and this is centred — because centring on the atom is what makes an added symbol
   * sit where a real one would.
   *
   * THE KNOCKOUT IS APPROXIMATE. A real renderer shortens the bonds meeting at a labelled atom;
   * we cannot, so the glyph carries a stroke in the surface colour to clear the lines running
   * under it. `--ws-card` is the usual surface and the rule lives in `.mx-sym`, which is one
   * line to retune once this has been looked at on both backgrounds.
   */
  const sizes = [...svg.matchAll(/<text[^>]*font-size="([-\d.]+)"/g)].map(m => Number(m[1]));
  const size = sizes.length ? Math.max(...sizes) : 14;
  const added: string[] = [];
  const edges: Point[] = [];
  for (const [key, colour] of Object.entries(colours)) {
    const index = Number(key);
    if (!colour || drawn.has(index)) continue;
    const atom = atoms[index];
    const symbol = symbols[index];
    if (!atom || !symbol) continue;
    const y = atom.y + size * 0.336;
    const ext = textExtent(symbol, size);
    edges.push({ x: atom.x - ext.half, y: y - ext.above }, { x: atom.x + ext.half, y: y + ext.below });
    added.push(`<text class="mx-sym" x="${atom.x.toFixed(2)}" y="${y.toFixed(2)}"`
      + ` font-size="${size}" stroke-width="${(size * 0.22).toFixed(2)}"`
      + ` text-anchor="middle" fill="${colour}">${escapeXml(symbol)}</text>`);
  }
  return added.length ? includeInViewBox(inject(tinted, added, 'mx-syms'), edges) : tinted;
}

/**
 * ELECTRONS ON A DRAWN ATOM — the same four positions the text mark offers.
 *
 * Four sides, one or two each, clockwise from the top: `reaction-lewis` owns that encoding and
 * this reads it, because a lone pair on a drawn oxygen and a lone pair on a written one are the
 * same claim and a teacher should not meet two notations for it.
 *
 * A pair is two dots set either side of the radius rather than one along it — along it, the
 * outer dot reads as further from the atom than the inner one and the pair stops looking like
 * a pair.
 */
export function withLewisDots(svg: string, electrons: Record<number, string>): string {
  const entries = Object.entries(electrons);
  if (!entries.length) return svg;
  const { atoms } = anchorPoints(svg);
  const unit = drawUnit({ atoms, bonds: [] });
  const out: string[] = [];
  const edges: Point[] = [];

  for (const [key, value] of entries) {
    const atom = atoms[Number(key)];
    if (!atom) continue;
    const dots = parseLewisValue(value);
    const sides: [number, number, number][] = [
      [dots.above, 0, -1], [dots.right, 1, 0], [dots.below, 0, 1], [dots.left, -1, 0],
    ];
    for (const [count, ux, uy] of sides) {
      if (!count) continue;
      const cx = atom.x + ux * unit * 1.45;
      const cy = atom.y + uy * unit * 1.45;
      /* Perpendicular to the direction the pair sticks out in. */
      const offsets = count === 2 ? [-unit * 0.38, unit * 0.38] : [0];
      for (const o of offsets) {
        const dx = cx - uy * o;
        const dy = cy + ux * o;
        const r = unit * 0.17;
        edges.push({ x: dx - r, y: dy - r }, { x: dx + r, y: dy + r });
        out.push(`<circle class="mx-e" cx="${dx.toFixed(2)}"`
          + ` cy="${dy.toFixed(2)}" r="${r.toFixed(2)}"/>`);
      }
    }
  }
  /* A lone pair sticks out at 1.45 units, which is outside a box cropped to the molecule. */
  return includeInViewBox(inject(svg, out, 'mx-electrons'), edges);
}

/**
 * THE TEACHER'S OWN NUMBER BESIDE AN ATOM, which is not the atom's index.
 *
 * A mechanism numbers the carbons along the chain; SMILES numbers them in the order they were
 * typed. The two agree often enough to be dangerous and differ often enough to be wrong, so
 * what is printed is what the teacher wrote — `withIndexLabels` prints the index and is for
 * CHOOSING an arrow's ends, never for the page.
 *
 * Set up and to the right, where a substituent is least likely to already be.
 */
export function withAtomLabels(svg: string, labels: Record<number, string>): string {
  const entries = Object.entries(labels);
  if (!entries.length) return svg;
  const at = anchorPoints(svg);
  const { atoms } = at;
  const bond = bondLength(at);
  const ends = bondEnds(at);
  const out: string[] = [];
  const edges: Point[] = [];
  for (const [key, text] of entries) {
    const atom = atoms[Number(key)];
    if (!atom || !text) continue;
    /* SIZED AGAINST THE ELEMENT SYMBOLS, not against the index pins.
       A pin in the arrow editor has a disc behind it and has to fit inside one; a number on
       the page sits beside a 14-unit element symbol and has to be read next to it. At the
       pins' own 1.15 units it drew as a speck. */
    /*
     * INTO THE GAP, not up and to the right.
     *
     * A fixed corner is right only where the atom happens to be free in that corner: on a ring
     * it lands on a bond, and on a crowded skeleton it lands nearer the next vertex than its
     * own. `freeDirection` finds the widest gap between the bonds leaving this atom, which is
     * where a chemist would write it.
     *
     * Half a bond out, so it clears both the vertex and any element symbol drawn on it, and
     * stays inside the half-bond that makes it unambiguously THIS atom's.
     */
    const angle = freeDirection(at, Number(key), ends);
    const x = atom.x + Math.cos(angle) * bond * 0.5;
    const y = atom.y + Math.sin(angle) * bond * 0.5;
    /*
     * MUCH SMALLER THAN AN ELEMENT SYMBOL — *"i'm pretty sure that these labels need to be
     * much smaller"*. The library sets its symbols at 14 units against a 24-unit bond; this is
     * a mark ON the structure rather than a part of it, and reads as one at about two thirds
     * the size.
     */
    const size = bond * 0.36;
    const ext = textExtent(text, size);
    edges.push({ x: x - ext.half, y: y - ext.above }, { x: x + ext.half, y: y + ext.below });
    out.push(`<text class="mx-label" x="${x.toFixed(2)}"`
      + ` y="${y.toFixed(2)}"`
      + ` font-size="${size.toFixed(2)}" text-anchor="middle">${escapeXml(text)}</text>`);
  }
  /* Set up and to the RIGHT of the atom, so an atom on either of those edges puts its number
     outside a box cropped to the molecule. This is the one that published wrong. */
  return includeInViewBox(inject(svg, out, 'mx-labels'), edges);
}

const escapeXml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function inject(svg: string, parts: string[], cls: string): string {
  if (!parts.length) return svg;
  const close = svg.lastIndexOf('</svg>');
  return close < 0 ? svg : `${svg.slice(0, close)}<g class="${cls}">${parts.join('')}</g>${svg.slice(close)}`;
}

/**
 * Everything a teacher has put on the drawing.
 *
 * Colours first — see the head of this file for why that is tidiness rather than the guarantee.
 */
export function annotateStructure(
  svg: string, a: StructureAnnotations, symbols: string[] = [],
): string {
  return withAtomLabels(
    withLewisDots(withAtomColours(svg, a.colours, symbols), a.electrons), a.labels);
}
